import React from "react";
import { FormCheckbox } from "protocol-setup/components/FormComponents/FormCheckboxGroup";
import FormTextarea from "protocol-setup/components/FormComponents/FormTextarea";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import labelsAndHelpText from "protocol-setup/constants/text";
import {
  completeSignOffUrl,
  defaultErrorMessage
} from "protocol-setup/redux/constants";
import {
  OpenErrorNotification,
  OpenSuccessNotification
} from "protocol-setup/components/notification";
import FormFileUpload from "protocol-setup/components/FormComponents/FormFileUpload";
import makeFileUploadRequest from "protocol-setup/redux/makeFileUploadRequest";
import ButtonGroupSignoff from "protocol-setup/components/FormComponents/ButtonGroupSignoff";
import errorMessages, { supportedFileTypes, COMMENTS_MAX_LENGTH } from "protocol-setup/constants/errorMessages";
import { dispatchRefreshEvent, useSetupPermission } from "protocol-setup/helpers";
import { calculateFilesToDelete, handleFileDeletions } from "protocol-setup/helpers/fileHelpers";
import { useRequestHistorySubmit } from "protocol-setup/helpers/withRequestHistory";
import ProtectionElements from 'protocol-setup/helpers/ProtectionElement';
import { tabNames } from "../orHelpers";

export const handleSubmitForm = async (
  data,
  requestNumber,
  psrsoId,
  onSuccessCallback,
  initialFiles = []
) => {
  try {
    const fd = new FormData();
    fd.append(
      "reqStr",
      JSON.stringify({
        psrsoId,
        setupCompletedProd: data.setupCompletedProd,
        formsActivatedProd: data.formsActivatedProd,
        comments: data.comments,
        requestNumber
      })
    );

    
    // Handle file uploads
    if (Array.isArray(data.files) && data.files.length > 0) {
      data.files.forEach((file) => {
        if (file instanceof Blob || file instanceof File) {
          const { name } = file;
          fd.append("files", file, name);
        }
      });
    }

    await makeFileUploadRequest(completeSignOffUrl, fd, "POST");

    const filesToDelete = calculateFilesToDelete(data.files, initialFiles);
    await handleFileDeletions(filesToDelete);

    if (onSuccessCallback) {
      await onSuccessCallback();
    }

    OpenSuccessNotification({
      message: labelsAndHelpText.openRave.completedForm.submitSuccess.message,
      autoHide: true
    });
  } catch (e) {
    const { message = "", error = "" } = e;
    OpenErrorNotification({
      message: error || message || defaultErrorMessage,
      autoHide: true
    });
  }
};

export const completedSignOffSchema = yup.object().shape({
  setupCompletedProd: yup.string().optional().nullable(),
  formsActivatedProd: yup.string().optional().nullable(),
  comments: yup
    .string()
    .max(COMMENTS_MAX_LENGTH, errorMessages.openRave.completedForm.comments.max)
    .optional()
    .nullable(),
  files: yup
    .array()
    .test(
      "fileFormat",
      labelsAndHelpText.openRave.completedForm.fileFormatError.message,
      (files) => {
        if (!files || files.length === 0) return true;
        const supportedFormats = supportedFileTypes
        return files.every(
          (file) =>
            !file ||
            supportedFormats.includes(file.name?.split(".").pop().toLowerCase())
        );
      }
    )
    .optional()
});

const getfiles = (docs) => docs?.map(({ DOC_FILE_NAME: name, DOCUMENT_ID: id }) => ({ name, id })) || [];

const CompletedForm = ({ protocolSetupData, fetchPsData }) => {
  const { requestNumber, signoffPending } = protocolSetupData || {};
  const { setupCompletedProd, formsActivatedProd, optionalTrackingComments } =
    signoffPending || {};
    
  const readOnly =
    setupCompletedProd || formsActivatedProd || optionalTrackingComments;
  const hasSignOffPermission = useSetupPermission(ProtectionElements.OPEN_SIGNOFF_COMPLETE);
  const isSignOffEditable = !readOnly && hasSignOffPermission;
  const completedDocsJson = signoffPending?.completedDocsJson || '[]';
  const {
    control,
    reset,
    handleSubmit,
    trigger,
    formState: { dirtyFields }
  } = useForm({
    resolver: yupResolver(completedSignOffSchema),
    defaultValues: {
      setupCompletedProd: setupCompletedProd ?? "",
      formsActivatedProd: formsActivatedProd ?? "",
      comments: optionalTrackingComments ?? "",
      files: getfiles(JSON.parse(completedDocsJson))
    }
  });

  const onSuccess = async () => {
    await fetchPsData();
    dispatchRefreshEvent();
  };

  // Wrap handleSubmitForm with request history tracking
  const onSubmitWithHistory = useRequestHistorySubmit(
    (data, onSuccessCallback) =>
      handleSubmitForm(
        data,
        requestNumber,
        signoffPending.psrsoId,
        onSuccessCallback,
        []
      ),
    dirtyFields,
    tabNames.SIGNOFF_COMPLETED,
    "Signoff"
  );

  const onSubmit = (data) => {
    if (readOnly) return;
    const d = { ...data, requestNumber };
    onSubmitWithHistory(d, onSuccess);
  };

  const onReset = () => {
    reset();
  };

  return (
    <div
      className={`form-layout-wrapper bg-transparent ${
        readOnly ? "read-only" : ""
      }`}
    >
      <form
        id="CompletedForm"
        className="bg-transparent mt-3"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="form-layout-body px-0">
          <span className="d-none"></span>
          <ul>
            <li className="row">
              <div className="col-4 mr-4 mb-0 form-sub-group">
                <div className="content-group-title">
                  {labelsAndHelpText.openRave.completedForm.groupTitle.label}
                </div>
                <FormCheckbox
                  className="pl-0 d-block"
                  labelClassName="color-dark-blue"
                  name="setupCompletedProd"
                  label={
                    labelsAndHelpText.openRave.completedForm.setupCompletedProd
                      .label
                  }
                  control={control}
                  readOnly={!isSignOffEditable}
                  disabled={!isSignOffEditable}
                  wrapperClassName={`mb-1 ${!isSignOffEditable ? 'read-only-show' : ''}`}
                  trigger={trigger}
                />
                <FormCheckbox
                  className="pl-0 d-block"
                  labelClassName="color-dark-blue"
                  name="formsActivatedProd"
                  label={
                    labelsAndHelpText.openRave.completedForm.formsActivatedProd
                      .label
                  }
                  control={control}
                  readOnly={!isSignOffEditable}
                  disabled={!isSignOffEditable}
                  wrapperClassName={`mb-3 ${!isSignOffEditable ? 'read-only-show' : ''}`}
                  trigger={trigger}
                />
                <FormTextarea
                  name="comments"
                  label={labelsAndHelpText.openRave.completedForm.comments.label}
                  control={control}
                  disabled={!isSignOffEditable}
                  data-testid="form-textarea-comments"
                  trigger={trigger}
                  wrapperClassName="mb-3"
                />
                <FormFileUpload
                  name="files"
                  label={`${labelsAndHelpText.openRave.completedForm.files.label}`}
                  control={control}
                  accept=".doc,.docx,.pdf"
                  multiple
                  disabled={!isSignOffEditable}
                />
              </div>
            </li>
          </ul>
        </div>
        <div className="form-layout-footer px-0 pb-0">
          <div className="form-layout-footer px-0 pb-0">
            {isSignOffEditable && (    
              <ButtonGroupSignoff
                onSubmit={onSubmit}
                onReset={onReset}
                control={control}
                saveLabel={
                  labelsAndHelpText.openRave.completedForm.saveButton.label
                }
                protectionElement= {ProtectionElements.OPEN_SIGNOFF_COMPLETE }
              />
             )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default CompletedForm;
